import StatusBadge from "@/components/base/StatusBadge";
import {
  ORDER_SOURCE_CONFIG,
  PAYMENT_CONFIG,
  PICKUP_CONFIG,
} from "@/constants/status";
import type { OrderRecord } from "@/types";
import { formatSAR } from "@/utils/format";

interface OrdersTableProps {
  orders: OrderRecord[];
  onSelect: (order: OrderRecord) => void;
}

export default function OrdersTable({ orders, onSelect }: OrdersTableProps) {
  if (orders.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
          <i className="ri-inbox-archive-line text-2xl"></i>
        </div>
        <p className="text-sm text-foreground-500">
          لا توجد طلبات مطابقة للفلتر الحالي
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1080px] text-right border-collapse">
        <thead>
          <tr className="text-xs text-foreground-500 bg-background-50">
            <th className="font-medium px-5 py-3 whitespace-nowrap">رقم الطلب</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الفرع</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">المصدر</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">وقت الطلب</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">المنتجات</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">المبلغ</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الحالة</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الاستلام</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الدفع</th>
            <th className="font-medium px-5 py-3 whitespace-nowrap">تفاصيل</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const payment = PAYMENT_CONFIG[order.payment];
            const pickup = PICKUP_CONFIG[order.pickup];
            const source = ORDER_SOURCE_CONFIG[order.source];
            return (
              <tr
                key={order.id}
                className="border-t border-background-100 hover:bg-primary-50/60 transition-colors duration-200"
              >
                <td className="px-5 py-3.5">
                  <span className="text-sm font-bold text-foreground-950">
                    #{order.id}
                  </span>
                  <p className="text-xs text-foreground-400 mt-0.5">
                    {order.customer}
                  </p>
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-700 whitespace-nowrap">
                  {order.branch}
                </td>
                <td className="px-3 py-3.5">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${source.className}`}
                    title={source.label}
                  >
                    <i className={source.icon}></i>
                    {source.short}
                  </span>
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-500 whitespace-nowrap">
                  {order.timeAgo}
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-700">
                  {order.items}
                </td>
                <td className="px-3 py-3.5 whitespace-nowrap">
                  <span className="block text-sm font-semibold text-foreground-900">
                    {formatSAR(order.amount)}
                  </span>
                  {order.deliveryFee !== undefined && order.deliveryFee > 0 && (
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-900"
                      title="رسوم التوصيل المحفوظة وقت إنشاء الطلب — تُحدَّد من إعدادات الفرع"
                    >
                      <i className="ri-e-bike-2-line"></i>
                      رسوم التوصيل {formatSAR(order.deliveryFee)}
                    </span>
                  )}
                  {order.appFee > 0 && (
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-900"
                      title="رسوم التطبيق ثابتة (٠.٨٠ ر.س) — تُحدَّد من الإدارة"
                    >
                      <i className="ri-smartphone-line"></i>
                      رسوم التطبيق {formatSAR(order.appFee)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3.5">
                  <StatusBadge status={order.status} pickup={order.pickup} />
                </td>
                <td className="px-3 py-3.5">
                  <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600 whitespace-nowrap">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                      <i className={pickup.icon}></i>
                    </span>
                    {pickup.label}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600 whitespace-nowrap">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                      <i className={payment.icon}></i>
                    </span>
                    {payment.label}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <button
                    type="button"
                    onClick={() => onSelect(order)}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-white transition-colors duration-200 cursor-pointer"
                    aria-label="عرض التفاصيل"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}